import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, useTheme } from "@mui/material";
import useStyles from "./summary-card.style";
import {
  MyCafeteriaPeriodLabel,
  MyCafeteriaSummaryCardErrorComponent,
  //MyCafeteriaSummaryChart,
} from "my-cafeteria/components";
import { OcChart, OcChartData, OcCurrency } from "oc/components";
import parseJdDate from "oc/utils/parse-jd-date";

/** Define the default height of the card */
const DIALOG_HEIGHT = "421px";

interface MyCafeteriaSummaryCardProps {
  data: {
    startDate: string;
    endDate: string;
    [key: string]: any;
  };
  loading: boolean;
  error: any;
  isMobile: boolean;
}

export const MyCafeteriaSummaryCard: React.FC<MyCafeteriaSummaryCardProps> = ({
  data,
  loading,
  error,
  isMobile,
}) => {
  /** Apply styles */
  const classes = useStyles({ height: DIALOG_HEIGHT, isMobile });
  const theme = useTheme();
  const { t } = useTranslation();

  const allCostsLabel = OcCurrency({
    value: data?.allCosts ?? 0,
  });

  const allBenefitsLabel = OcCurrency({
    value: data?.allBenefits ?? 0,
  });

  const availableAmountLabel = OcCurrency({
    value: data?.availableAmount ?? 0,
  });

  const allAmountLabel = OcCurrency({
    value: data?.allAmount ?? 0,
  });

  const chartData: OcChartData[] | null = useMemo(() => {
    const returnData: OcChartData[] = [
      {
        label: t("MY_CAFETERIA_ALL_AMOUNT"),
        color: theme.palette.primary.main,
        value: data?.allBenefits ?? 0,
        valueInLegend: allBenefitsLabel,
      },
      {
        label: t("MY_CAFETERIA_ALL_COSTS"),
        color: theme.palette.yellowBase.main,
        value: data?.allCosts ?? 0,
        valueInLegend: allCostsLabel,
      },
      {
        label: t("MY_CAFETERIA_REMAINS"),
        color: theme.palette.greenBase.main,
        value: data?.availableAmount ?? 0,
        valueInLegend: availableAmountLabel,
      },
    ];

    return returnData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /** Define error handling and error component */
  if (error) return <MyCafeteriaSummaryCardErrorComponent error={error} />;

  return (
    <Card
      id="my-cafeteria-summary-card"
      variant="outlined"
      classes={{ root: classes.root }}
    >
      <Box
        id="my-cafeteria-summary-card-header"
        display="flex"
        flexDirection="row"
        justifyContent="center"
      >
        <MyCafeteriaPeriodLabel
          loading={loading}
          startDate={parseJdDate(data?.startDate) ?? new Date()}
          endDate={parseJdDate(data?.endDate) ?? new Date()}
        />
      </Box>
      <Box id="my-cafeteria-summary-card-divider" className={classes.divider} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <OcChart
          data={chartData}
          summaryTitle={allAmountLabel}
          summaryText={t("MY_CAFETERIA_BUDGET")}
          variant="MyCafeteria"
          loading={loading}
        />
      </Box>
    </Card>
  );
};

export default MyCafeteriaSummaryCard;
